import { Alert, Dialog } from "@tm/components"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocalization } from "@tm/localization"
import { CustomArticleItemToAdd, CustomArticleItemToEdit } from "@tm/data/basket"
import { FormProvider, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Override } from "@tm/models"
import { Overwrite } from "@tm/utils"
import { useArticleFormDefaults } from "@bundles/basket/hooks/Forms/useArticleFormDefaults"
import { EditDialogBody } from "./EditDialogBody"
import { EditDialogHeader } from "./EditDialogHeader"
import { useSchema } from "./schema"
import { useOwnArticlesStore } from "../store"

export type DialogCustomArticleAdd = CustomArticleItemToAdd & { type: "add" }
export type DialogCustomArticleEdit = CustomArticleItemToEdit & { type: "edit" }
export type DialogCustomArticle = DialogCustomArticleAdd | DialogCustomArticleEdit

type EditDialogContentProps = {
    article: DialogCustomArticle
    disabledCategory?: boolean
    note?: string
    saveError?: string
    onClose(): void
    onSave(article: DialogCustomArticle, note?: string): void
}

function EditDialogContent({ onClose, onSave, article, note, saveError }: EditDialogContentProps) {
    const { translateText } = useLocalization()
    const schema = useSchema()
    const { selectedCompilation } = useOwnArticlesStore()
    const { vatRateItems } = useArticleFormDefaults()

    const headline = article.type === "edit" ? translateText(757) : translateText(13906)

    const defaultValues = useMemo(() => {
        if (article.type === "add") {
            return {
                description: "",
                vatType: article.selectedVatRate.vatType,
                categoryId: article.categoryId,
                customArticleListId: selectedCompilation?.id,
            }
        }
        return {
            ...article,
            vatType: article.selectedVatRate.vatType,
            note,
        }
    }, [article, note])

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const { handleSubmit } = form

    function handleConfirmButtonClick(data: Overwrite<z.infer<typeof schema>, { customArticleListId?: string }>) {
        const { note: noteToSave, vatType, ...articleData } = data
        let selectedVatRate = vatRateItems.find((x) => x.vatRate.vatType === vatType)?.vatRate
        if (!selectedVatRate) {
            selectedVatRate = { vatType: 0, vatValue: 0 }
        }
        selectedVatRate.isSelected = true
        onSave({ ...article, ...articleData, selectedVatRate }, noteToSave)
    }

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                <EditDialogHeader headline={headline} onCloseButtonClick={onClose} sx={{ mb: 2 }} />
                {saveError && <Alert size="small" severity="error" title={saveError} variant="outlined" sx={{ mb: 2 }} />}
                <EditDialogBody />
            </form>
        </FormProvider>
    )
}

type EditDialogProps = Override<
    EditDialogContentProps,
    {
        article?: EditDialogContentProps["article"]
    }
>

export function ArticleEditDialog(props: EditDialogProps) {
    const [open, setOpen] = useState(false)

    const { article, onClose, ...restProps } = props

    const [editableArticle, setEditableArticle] = useState<DialogCustomArticle | undefined>()

    const handleClose = useCallback(() => {
        setOpen(false)
    }, [])

    const handleExited = useCallback(() => {
        setEditableArticle(undefined)
        onClose()
    }, [])

    useEffect(() => {
        if (article) {
            setOpen(true)
            setEditableArticle(article)
        } else {
            handleClose()
        }
    }, [article])

    return (
        <Dialog open={open} position="middle" maxWidth="lg" onExited={handleExited}>
            {editableArticle && <EditDialogContent {...restProps} article={editableArticle} onClose={handleClose} />}
        </Dialog>
    )
}
